export const GUARANTEE_QUILL_MODULES = {
  toolbar: [
    [{ header: "1" }, { header: "2" }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link"],
    ["clean"],
  ],
  clipboard: {
    matchVisual: false,
  },
};

export const GUARANTEE_QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
];

const RICH_TEXT_TAG_PATTERN =
  /<\/?(p|br|ul|ol|li|strong|em|u|s|h[1-6]|blockquote|a|span|div)(\s|>|\/)/i;

export function stripHtmlToPlainText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/p>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

export function isEmptyRichText(html: string): boolean {
  return stripHtmlToPlainText(html).replace(/\s+/g, "").length === 0;
}

export function normalizeGuaranteeDescriptionForEditor(
  description: string | null | undefined
): string {
  if (description == null) return "";

  const trimmed = String(description).trim();

  if (!trimmed || RICH_TEXT_TAG_PATTERN.test(trimmed)) {
    return trimmed;
  }

  return trimmed
    .split(/\n{2,}/)
    .map((paragraph) => {
      const withBreaks = escapeHtml(paragraph).replace(/\n/g, "<br />");
      return `<p>${withBreaks}</p>`;
    })
    .join("");
}

export function pickNonEmptyDescription(
  ...candidates: Array<string | null | undefined>
): string {
  for (const candidate of candidates) {
    const normalized = normalizeGuaranteeDescriptionForEditor(candidate);
    if (!isEmptyRichText(normalized)) {
      return normalized;
    }
  }
  return "";
}

export type GuaranteeFormRecord = {
  id?: number;
  name?: string;
  description?: string;
  documents?: string[];
};

function asGuaranteeRecord(value: unknown): GuaranteeFormRecord | null {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  const description =
    typeof row.description === "string"
      ? row.description
      : row.description == null
      ? undefined
      : String(row.description);
  const name = typeof row.name === "string" ? row.name : undefined;
  const documents = Array.isArray(row.documents)
    ? row.documents.filter((item): item is string => typeof item === "string")
    : undefined;
  const id = row.id != null ? Number(row.id) : undefined;

  return { id, name, description, documents };
}

/**
 * Accepts the usual ADM envelopes: `{ data: Guarantee }`, `{ data: Guarantee[] }`
 * or `{ data: { data: Guarantee[] } }`.
 */
export function unwrapGuaranteeRecord(
  payload: unknown,
  id: number
): GuaranteeFormRecord | null {
  const root = payload as Record<string, unknown> | null;
  const data = (root?.data ?? payload) as unknown;

  if (Array.isArray(data)) {
    const found = data.find((item) => Number((item as { id?: unknown })?.id) === id);
    return asGuaranteeRecord(found);
  }

  if (data && typeof data === "object" && Array.isArray((data as { data?: unknown }).data)) {
    const nested = (data as { data: unknown[] }).data.find(
      (item) => Number((item as { id?: unknown })?.id) === id
    );
    return asGuaranteeRecord(nested);
  }

  const record = asGuaranteeRecord(data);
  if (record && (record.id == null || record.id === id || record.description != null)) {
    return record;
  }

  return null;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
